import { PermissionLevel } from "../constants";

export interface IUser {
  id: number;
  name: string;
  email: string;
  group_ids: number[];
  organization_role: "admin" | "member" | "fybr_admin";
  deleted_at?: string | null;
}

export interface IGroup {
  id: number;
  name: string;
  user_ids: number[];
  autogenerated: boolean;
  folder_label?: string;
  permission_label?: string;
}

export interface IInvitableGroup {
  id: number;
  name: string;
  autogenerated: boolean;
  folder_id: number;
  folder_label: string;
  permission_label: string;
  level: number;
}

export interface IPermissionType {
  id: number;
  level: number;
  label: string;
}

export interface IFolderPermission {
  id: number;
  permission_type_id: number;
  user?: { id: number; name: string; email: string };
  group?: { id: number; name: string };
  level: number;
  label: string;
}

export interface IComputedFolderPermission {
  user: {
    id: number;
    name: string;
    email: string;
  };
  access_level: PermissionLevel;
}

export interface IFolder {
  id: number;
  label: string;
  user_ids: number[];
  group_ids: number[];
  organization_order_type_ids: number[];
  deleted_at?: string | null;
}

export interface IOrganization {
  id: number;
  name: string;
  units?: "metric" | "sae";
  basemap?: string;
  location?: {
    type: "Point";
    coordinates: [number, number]; // [longitude, latitude]
  };
  generate_xls?: boolean;
  material_ids?: number[];
  site_ids?: number[];
  allowed_invite_domains?: string[];
}

export interface IMaterial {
  id: number;
  name: string;
  organization_id?: number;
  archived?: number | null;
  inventory: boolean;
}

export interface ISite {
  id: number;
  label: string;
  deleted_at?: string | null;
  location?: {
    type: "Point";
    coordinates: [number, number]; // [longitude, latitude]
  };
}

export interface IOrderType {
  id: number;
  name: string;
  description?: string;
  items?: string[];
  deleted_at?: string | null;
  assignment_id?: number;
  site_id?: number | null;
  site_label?: string | null;
  order_count?: number;
  /**
   * Deadline in seconds. Always set on both global templates and org assignment
   * rows (instances snapshot the template deadline at assignment time).
   */
  deadline_duration_seconds?: number;
}

export interface IChartProperty {
  id: number;
  label: string;
  deleted_at?: string | null;
  created_at?: string;
}

export interface IOrgChartPropertyAssignment {
  assignment_id: number;
  organization_id: number;
  chart_property_id: number;
  layer_name: string;
  label: string;
  deleted_at?: string | null;
  created_at?: string;
}

export interface IActivateFolderResponse {
  message: string;
  folderId: number;
}

export interface IActivateSiteResponse {
  message: string;
  siteId: number;
}

export class ApiError extends Error {
  code?: string;
  status: number;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

async function fetchJson<T>(
  url: string,
  init: RequestInit,
  errorPrefix: string
): Promise<T> {
  const response = await fetch(url, {
    ...init,
    headers: { Accept: "application/json, text/plain, */*", ...init.headers },
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      errorData.error ||
        `${errorPrefix}: ${response.status} ${response.statusText}`,
      response.status,
      errorData.code
    );
  }
  return response.json();
}

class ApiAdmin {
  public static getUsers(
    organizationId: number,
    includeDeactivated: boolean = false
  ): Promise<{ users: IUser[] }> {
    const url = new URL(
      `/api/v1/admin/organizations/${organizationId}/users`,
      window.location.origin
    );
    if (includeDeactivated) {
      url.searchParams.set("include_deactivated", "true");
    }
    return fetchJson<{ users: IUser[] }>(
      url.toString(),
      { method: "GET" },
      "Failed to fetch admin users"
    );
  }

  public static updateUser(
    organizationId: number,
    id: number,
    data: Partial<IUser>
  ): Promise<any> {
    return fetchJson(
      `/api/v1/admin/organizations/${organizationId}/users/${id}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
      "Failed to update user"
    );
  }

  public static updateUserRole(
    organizationId: number,
    userId: number,
    role: "admin" | "member" | "fybr_admin"
  ): Promise<any> {
    return fetchJson(
      `/api/v1/admin/organizations/${organizationId}/users/${userId}/role`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ organization_role: role }),
      },
      "Failed to update user role"
    );
  }

  public static deleteUser(organizationId: number, id: number): Promise<any> {
    return fetchJson(
      `/api/v1/admin/organizations/${organizationId}/users/${id}`,
      { method: "DELETE" },
      "Failed to delete user"
    );
  }

  public static activateUser(
    organizationId: number,
    userId: number
  ): Promise<any> {
    return fetchJson(
      `/api/v1/admin/organizations/${organizationId}/users/${userId}/activate`,
      { method: "POST" },
      "Failed to activate user"
    );
  }

  public static createUser(
    organizationId: number,
    data: Omit<IUser, "id">
  ): Promise<any> {
    return fetchJson(
      `/api/v1/admin/organizations/${organizationId}/users`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
      "Failed to create user"
    );
  }

  public static getGroups(
    organizationId: number
  ): Promise<{ groups: IGroup[] }> {
    return fetchJson<{ groups: IGroup[] }>(
      `/api/v1/admin/organizations/${organizationId}/groups`,
      { method: "GET" },
      "Failed to fetch admin groups"
    );
  }

  public static updateGroup(
    organizationId: number,
    id: number,
    data: Partial<IGroup>
  ): Promise<any> {
    return fetchJson(
      `/api/v1/admin/organizations/${organizationId}/groups/${id}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
      "Failed to update group"
    );
  }

  public static deleteGroup(organizationId: number, id: number): Promise<any> {
    return fetchJson(
      `/api/v1/admin/organizations/${organizationId}/groups/${id}`,
      { method: "DELETE" },
      "Failed to delete group"
    );
  }

  public static createGroup(
    organizationId: number,
    data: Omit<IGroup, "id">
  ): Promise<any> {
    return fetchJson(
      `/api/v1/admin/organizations/${organizationId}/groups`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
      "Failed to create group"
    );
  }

  public static getFolders(
    organizationId: number,
    includeDeactivated: boolean = false
  ): Promise<{ folders: IFolder[] }> {
    const url = new URL(
      `/api/v1/admin/organizations/${organizationId}/folders`,
      window.location.origin
    );
    if (includeDeactivated) {
      url.searchParams.set("include_deactivated", "true");
    }
    return fetchJson<{ folders: IFolder[] }>(
      url.toString(),
      { method: "GET" },
      "Failed to fetch admin folders"
    );
  }

  public static updateFolder(
    organizationId: number,
    id: number,
    data: Partial<IFolder>
  ): Promise<any> {
    return fetchJson(
      `/api/v1/admin/organizations/${organizationId}/folders/${id}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
      "Failed to update folder"
    );
  }

  public static deleteFolder(organizationId: number, id: number): Promise<any> {
    return fetchJson(
      `/api/v1/admin/organizations/${organizationId}/folders/${id}`,
      { method: "DELETE" },
      "Failed to delete folder"
    );
  }

  public static activateFolder(
    organizationId: number,
    id: number
  ): Promise<IActivateFolderResponse> {
    return fetchJson<IActivateFolderResponse>(
      `/api/v1/admin/organizations/${organizationId}/folders/${id}/activate`,
      { method: "POST" },
      "Failed to activate folder"
    );
  }

  public static createFolder(
    organizationId: number,
    data: Omit<IFolder, "id">
  ): Promise<any> {
    return fetchJson(
      `/api/v1/admin/organizations/${organizationId}/folders`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
      "Failed to create folder"
    );
  }

  public static getPermissionTypes(organizationId: number): Promise<{
    permission_types: IPermissionType[];
  }> {
    return fetchJson<{ permission_types: IPermissionType[] }>(
      `/api/v1/admin/organizations/${organizationId}/permissions/types`,
      { method: "GET" },
      "Failed to fetch admin permission types"
    );
  }

  public static getFolderPermissions(
    organizationId: number,
    folderId: number
  ): Promise<{
    permissions: IFolderPermission[];
    user_permissions: IComputedFolderPermission[];
  }> {
    return fetchJson<{
      permissions: IFolderPermission[];
      user_permissions: IComputedFolderPermission[];
    }>(
      `/api/v1/admin/organizations/${organizationId}/folders/${folderId}/permissions`,
      { method: "GET" },
      "Failed to fetch admin folder permissions"
    );
  }

  public static updateFolderPermissions(
    organizationId: number,
    folderId: number,
    data: {
      user_permissions: Array<{ user_id: number; access_level: number }>;
      group_permissions: Array<{
        group_id: number;
        access_level: number;
      }>;
    }
  ): Promise<any> {
    return fetchJson(
      `/api/v1/admin/organizations/${organizationId}/folders/${folderId}/permissions`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
      "Failed to update folder permissions"
    );
  }

  public static getFolderPermissionsForFolderAdmin(folderId: number): Promise<{
    permissions: IFolderPermission[];
    user_permissions: IComputedFolderPermission[];
  }> {
    return fetchJson<{
      permissions: IFolderPermission[];
      user_permissions: IComputedFolderPermission[];
    }>(
      `/api/v1/folders/${folderId}/permissions`,
      { method: "GET" },
      "Failed to fetch folder permissions"
    );
  }

  public static updateFolderPermissionsForFolderAdmin(
    folderId: number,
    data: { user_permissions: Array<{ user_id: number; access_level: number }> }
  ): Promise<any> {
    return fetchJson(
      `/api/v1/folders/${folderId}/permissions`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
      "Failed to update folder permissions"
    );
  }

  public static updateFolderForFolderAdmin(
    folderId: number,
    data: { label: string }
  ): Promise<{
    message: string;
    folder: { id: number; label: string };
  }> {
    return fetchJson(
      `/api/v1/folders/${folderId}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
      "Failed to update folder"
    );
  }

  public static deactivateFolderForFolderAdmin(
    folderId: number
  ): Promise<{ message: string; folderId: number }> {
    return fetchJson(
      `/api/v1/folders/${folderId}`,
      { method: "DELETE" },
      "Failed to delete folder"
    );
  }

  public static getDeactivatedFoldersForFolderAdmin(): Promise<{
    folders: Array<{ id: number; label: string; deleted_at: string }>;
  }> {
    return fetchJson(
      "/api/v1/folders/deactivated-for-admin",
      { method: "GET" },
      "Failed to fetch deactivated folders"
    );
  }

  public static reactivateFolderForFolderAdmin(
    folderId: number
  ): Promise<{ message: string; folderId: number }> {
    return fetchJson(
      `/api/v1/folders/${folderId}/reactivate`,
      { method: "POST" },
      "Failed to reactivate folder"
    );
  }

  public static getInvitableGroupsForFolderAdmin(): Promise<{
    groups: IInvitableGroup[];
    allowed_invite_domains: string[];
  }> {
    return fetchJson(
      "/api/v1/folders/invitable-groups",
      { method: "GET" },
      "Failed to fetch invitable groups"
    );
  }

  public static inviteUserForFolderAdmin(data: {
    name: string;
    email: string;
    group_ids: number[];
  }): Promise<{
    message: string;
    user: {
      id: number;
      name: string;
      email: string;
      organization_role: string;
    };
  }> {
    return fetchJson(
      "/api/v1/folders/invite-user",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
      "Failed to invite user"
    );
  }

  public static getPermissionTypesForFolderAdmin(
    folderId: number
  ): Promise<{ permission_types: IPermissionType[] }> {
    return fetchJson<{ permission_types: IPermissionType[] }>(
      `/api/v1/folders/${folderId}/permission-types`,
      { method: "GET" },
      "Failed to fetch permission types"
    );
  }

  public static getOrgUsersForFolderAdmin(folderId: number): Promise<{
    users: IUser[];
  }> {
    return fetchJson<{ users: IUser[] }>(
      `/api/v1/folders/${folderId}/org-users`,
      { method: "GET" },
      "Failed to fetch organization users"
    );
  }

  public static getAdminOrganizations(): Promise<{
    organizations: IOrganization[];
  }> {
    return fetchJson<{ organizations: IOrganization[] }>(
      "/api/v1/admin/organizations",
      { method: "GET" },
      "Failed to fetch admin organizations"
    );
  }

  public static getOrganizations(): Promise<{
    organizations: IOrganization[];
  }> {
    return fetchJson<{ organizations: IOrganization[] }>(
      "/api/v1/internal/organizations",
      { method: "GET" },
      "Failed to fetch organizations"
    );
  }

  public static updateOrganization(
    id: number,
    data: Partial<
      Omit<IOrganization, "location"> & {
        location?: { latitude: number; longitude: number };
      }
    >
  ): Promise<any> {
    return fetchJson(
      `/api/v1/internal/organizations/${id}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
      "Failed to update organization"
    );
  }

  public static createOrganization(
    data: Omit<IOrganization, "id">
  ): Promise<any> {
    return fetchJson(
      "/api/v1/internal/organizations",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
      "Failed to create organization"
    );
  }

  public static getOrganization(
    id: number
  ): Promise<{ organization: IOrganization }> {
    return fetchJson<{ organization: IOrganization }>(
      `/api/v1/internal/organizations/${id}`,
      { method: "GET" },
      "Failed to fetch organization"
    );
  }

  public static getAllMaterials(): Promise<{ materials: IMaterial[] }> {
    return fetchJson<{ materials: IMaterial[] }>(
      "/api/v1/internal/materials",
      { method: "GET" },
      "Failed to fetch materials"
    );
  }

  public static getMaterialsForOrganization(
    organizationId: number
  ): Promise<{ materials: IMaterial[] }> {
    return fetchJson<{ materials: IMaterial[] }>(
      `/api/v1/internal/organizations/${organizationId}/materials`,
      { method: "GET" },
      "Failed to fetch materials"
    );
  }

  public static getAllSites(): Promise<{ sites: ISite[] }> {
    return fetchJson<{ sites: ISite[] }>(
      "/api/v1/internal/sites",
      { method: "GET" },
      "Failed to fetch sites"
    );
  }

  public static createMaterial(
    orgId: number,
    data: { name: string; inventory?: boolean }
  ): Promise<{ material: IMaterial }> {
    return fetchJson<{ material: IMaterial }>(
      `/api/v1/internal/organizations/${orgId}/materials`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
      "Failed to create material"
    );
  }

  public static createSite(
    orgId: number,
    data: { label: string; location?: { latitude: number; longitude: number } }
  ): Promise<{ site: ISite }> {
    return fetchJson<{ site: ISite }>(
      `/api/v1/internal/organizations/${orgId}/sites`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
      "Failed to create site"
    );
  }

  public static updateMaterial(
    orgId: number,
    materialId: number,
    data: { name?: string; archived?: number | null; inventory?: boolean }
  ): Promise<{ material: IMaterial }> {
    return fetchJson<{ material: IMaterial }>(
      `/api/v1/internal/organizations/${orgId}/materials/${materialId}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
      "Failed to update material"
    );
  }

  public static updateSite(
    orgId: number,
    siteId: number,
    data: { label?: string; location?: { latitude: number; longitude: number } }
  ): Promise<{ site: ISite }> {
    return fetchJson<{ site: ISite }>(
      `/api/v1/internal/organizations/${orgId}/sites/${siteId}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
      "Failed to update site"
    );
  }

  public static getSitesForOrganization(
    organizationId: number,
    includeDeactivated: boolean = false
  ): Promise<{ sites: ISite[] }> {
    const url = new URL(
      `/api/v1/internal/organizations/${organizationId}/sites`,
      window.location.origin
    );
    if (includeDeactivated) {
      url.searchParams.set("include_deactivated", "true");
    }
    return fetchJson<{ sites: ISite[] }>(
      url.toString(),
      { method: "GET" },
      "Failed to fetch sites for organization"
    );
  }

  public static activateSite(
    organizationId: number,
    siteId: number
  ): Promise<IActivateSiteResponse> {
    return fetchJson<IActivateSiteResponse>(
      `/api/v1/internal/organizations/${organizationId}/sites/${siteId}/activate`,
      { method: "POST" },
      "Failed to activate site"
    );
  }

  public static getFolderMaterials(
    organizationId: number,
    folderId: number
  ): Promise<{ material_ids: number[] }> {
    return fetchJson<{ material_ids: number[] }>(
      `/api/v1/admin/organizations/${organizationId}/folders/${folderId}/materials`,
      { method: "GET" },
      "Failed to fetch folder materials"
    );
  }

  public static updateFolderMaterials(
    organizationId: number,
    folderId: number,
    data: { material_ids: number[] }
  ): Promise<any> {
    return fetchJson(
      `/api/v1/admin/organizations/${organizationId}/folders/${folderId}/materials`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
      "Failed to update folder materials"
    );
  }

  public static getFolderOrderTypes(
    organizationId: number,
    folderId: number
  ): Promise<{ organization_order_type_ids: number[] }> {
    return fetchJson<{ organization_order_type_ids: number[] }>(
      `/api/v1/admin/organizations/${organizationId}/folders/${folderId}/order_types`,
      { method: "GET" },
      "Failed to fetch folder order types"
    );
  }

  public static updateFolderOrderTypes(
    organizationId: number,
    folderId: number,
    data: { organization_order_type_ids: number[] }
  ): Promise<any> {
    return fetchJson(
      `/api/v1/admin/organizations/${organizationId}/folders/${folderId}/order_types`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
      "Failed to update folder order types"
    );
  }

  public static getAllOrderTypes(
    includeDeactivated: boolean = false
  ): Promise<{ order_types: IOrderType[] }> {
    const url = new URL("/api/v1/internal/order-types", window.location.origin);
    if (includeDeactivated) {
      url.searchParams.set("include_deactivated", "true");
    }
    return fetchJson<{ order_types: IOrderType[] }>(
      url.toString(),
      { method: "GET" },
      "Failed to fetch order types"
    );
  }

  public static createOrderType(data: {
    name: string;
    description?: string;
    items?: string[];
    deadline_duration_seconds?: number;
  }): Promise<{ order_type: IOrderType }> {
    return fetchJson<{ order_type: IOrderType }>(
      "/api/v1/internal/order-types",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
      "Failed to create order type"
    );
  }

  public static updateOrderType(
    id: number,
    data: {
      name?: string;
      description?: string;
      items?: string[];
      deadline_duration_seconds?: number;
    }
  ): Promise<{ order_type: IOrderType }> {
    return fetchJson<{ order_type: IOrderType }>(
      `/api/v1/internal/order-types/${id}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
      "Failed to update order type"
    );
  }

  public static deactivateOrderType(id: number): Promise<any> {
    return fetchJson(
      `/api/v1/internal/order-types/${id}`,
      { method: "DELETE" },
      "Failed to deactivate order type"
    );
  }

  public static activateOrderType(id: number): Promise<any> {
    return fetchJson(
      `/api/v1/internal/order-types/${id}/activate`,
      { method: "POST" },
      "Failed to activate order type"
    );
  }

  public static getOrderTypesForOrganization(
    orgId: number,
    includeDeactivated: boolean = false
  ): Promise<{ order_types: IOrderType[] }> {
    const url = new URL(
      `/api/v1/internal/organizations/${orgId}/order-types`,
      window.location.origin
    );
    if (includeDeactivated) {
      url.searchParams.set("include_deactivated", "true");
    }
    return fetchJson<{ order_types: IOrderType[] }>(
      url.toString(),
      { method: "GET" },
      "Failed to fetch order types for organization"
    );
  }

  public static assignOrderTypeToOrganization(
    orgId: number,
    orderTypeId: number,
    siteId: number | null = null
  ): Promise<any> {
    return fetchJson(
      `/api/v1/internal/organizations/${orgId}/order-types/${orderTypeId}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ site_id: siteId }),
      },
      "Failed to assign order type"
    );
  }

  public static deactivateOrderTypeAssignment(
    orgId: number,
    assignmentId: number
  ): Promise<any> {
    return fetchJson(
      `/api/v1/internal/organizations/${orgId}/order-type-assignments/${assignmentId}`,
      { method: "DELETE" },
      "Failed to deactivate order type assignment"
    );
  }

  public static activateOrderTypeAssignment(
    orgId: number,
    assignmentId: number
  ): Promise<any> {
    return fetchJson(
      `/api/v1/internal/organizations/${orgId}/order-type-assignments/${assignmentId}/activate`,
      { method: "POST" },
      "Failed to activate order type assignment"
    );
  }

  public static updateOrderTypeAssignment(
    orgId: number,
    assignmentId: number,
    data: {
      site_id?: number | null;
      deadline_duration_seconds?: number;
      name?: string;
      description?: string;
      items?: string[];
    }
  ): Promise<any> {
    return fetchJson(
      `/api/v1/internal/organizations/${orgId}/order-type-assignments/${assignmentId}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
      "Failed to update order type assignment"
    );
  }

  public static getChartProperties(
    includeDeactivated: boolean = false
  ): Promise<{ chart_properties: IChartProperty[] }> {
    const url = new URL(
      "/api/v1/internal/chart-properties",
      window.location.origin
    );
    if (includeDeactivated) {
      url.searchParams.set("include_deactivated", "true");
    }
    return fetchJson<{ chart_properties: IChartProperty[] }>(
      url.toString(),
      { method: "GET" },
      "Failed to fetch chart properties"
    );
  }

  public static createChartProperty(data: {
    label: string;
  }): Promise<{ chart_property: IChartProperty }> {
    return fetchJson<{ chart_property: IChartProperty }>(
      "/api/v1/internal/chart-properties",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
      "Failed to create chart property"
    );
  }

  public static updateChartProperty(
    id: number,
    data: { label?: string }
  ): Promise<{ chart_property: IChartProperty }> {
    return fetchJson<{ chart_property: IChartProperty }>(
      `/api/v1/internal/chart-properties/${id}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
      "Failed to update chart property"
    );
  }

  public static deactivateChartProperty(id: number): Promise<any> {
    return fetchJson(
      `/api/v1/internal/chart-properties/${id}`,
      { method: "DELETE" },
      "Failed to deactivate chart property"
    );
  }

  public static activateChartProperty(id: number): Promise<any> {
    return fetchJson(
      `/api/v1/internal/chart-properties/${id}/activate`,
      { method: "POST" },
      "Failed to activate chart property"
    );
  }

  public static getChartPropertiesForOrganization(
    orgId: number,
    includeDeactivated: boolean = false
  ): Promise<{ chart_properties: IOrgChartPropertyAssignment[] }> {
    const url = new URL(
      `/api/v1/internal/organizations/${orgId}/chart-properties`,
      window.location.origin
    );
    if (includeDeactivated) {
      url.searchParams.set("include_deactivated", "true");
    }
    return fetchJson<{ chart_properties: IOrgChartPropertyAssignment[] }>(
      url.toString(),
      { method: "GET" },
      "Failed to fetch organization chart properties"
    );
  }

  public static assignChartPropertyToOrganization(
    orgId: number,
    chartPropertyId: number,
    layerName: string
  ): Promise<any> {
    return fetchJson(
      `/api/v1/internal/organizations/${orgId}/chart-properties/${chartPropertyId}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ layer_name: layerName }),
      },
      "Failed to assign chart property"
    );
  }

  public static removeChartPropertyAssignment(
    orgId: number,
    assignmentId: number
  ): Promise<any> {
    return fetchJson(
      `/api/v1/internal/organizations/${orgId}/chart-property-assignments/${assignmentId}`,
      { method: "DELETE" },
      "Failed to remove chart property assignment"
    );
  }

  public static activateChartPropertyAssignment(
    orgId: number,
    assignmentId: number
  ): Promise<any> {
    return fetchJson(
      `/api/v1/internal/organizations/${orgId}/chart-property-assignments/${assignmentId}/activate`,
      { method: "POST" },
      "Failed to activate chart property assignment"
    );
  }
}

export default ApiAdmin;
